import { useCallback, useEffect, useState } from "react"
import { toast } from "react-toastify"
import {
  useCompanyConnectorControllerCompleteAirbyteSetupMutation,
  useCompanyConnectorControllerGetAirbytePropertiesQuery,
  useCompanyConnectorControllerGetAirbyteSetupErrorQuery,
  useCompanyConnectorControllerGetCompanyConnectorsQuery,
} from "~/ui-rtk/api/companyConnectorApi"
import { ConnectorDto, ConnectionStatus } from "~/ui-rtk/api/types"
import { useAirbytePollingInterval } from "~/ui-rtk/hooks/airbyte-polling"
import {
  AIRBYTE_POLLING_PREFIX,
  startAirbytePolling,
  stopAirbytePolling,
} from "~/ui-rtk/utils/airbyte-polling"
import { isAirbyteConnector } from "~/ui-rtk/utils/connector-utils"
import { hasErrorMessage } from "~/ui-rtk/utils/http-utils"

/**
 * Hook for handling Airbyte connector additional configuration
 * Supports multiple connector types with different additional config requirements (GA4, Facebook Ads, etc.)
 */
export const useAirbyteAdditionalConfig = (
  companyConnectorId: string,
  connector?: ConnectorDto,
) => {
  const [completeAirbyteSetup, { isLoading: isCompletingSetup }] =
    useCompanyConnectorControllerCompleteAirbyteSetupMutation()

  const [hasFailed, setHasFailed] = useState(false)

  // Poll connector status via RTK Query's pollingInterval; the shared hook
  // returns a non-zero value only while at least one Airbyte setup is active
  // in sessionStorage (see utils/airbyte-polling). Subscribers dedupe, so the
  // network sees at most one request per tick even when the manager is also
  // mounted.
  const pollingInterval = useAirbytePollingInterval()
  const { data: connectors } =
    useCompanyConnectorControllerGetCompanyConnectorsQuery(undefined, {
      pollingInterval,
    })

  // Fetch schema and metadata for Airbyte connector
  // - additional_config: Defines what inputs are needed
  // - metadata: Existing values if connector was previously configured
  const {
    data: airbyteProperties,
    isLoading: isLoadingProperties,
    error: propertiesError,
  } = useCompanyConnectorControllerGetAirbytePropertiesQuery(
    { id: companyConnectorId },
    {
      skip: !companyConnectorId || !connector || !isAirbyteConnector(connector),
    },
  )
  const { data: errorData } =
    useCompanyConnectorControllerGetAirbyteSetupErrorQuery(
      { id: companyConnectorId },
      {
        skip: !hasFailed || !companyConnectorId,
      },
    )

  // Detect when background process fails (status changes to PENDING_RECONNECT)
  // or succeeds (CONNECTED). Runs whenever connector data updates — which
  // happens automatically because the shared RTK Query cache is kept fresh by
  // the polling manager and our own subscription above.
  useEffect(() => {
    if (!connectors || !companyConnectorId) return

    const isActivelyPolling =
      sessionStorage.getItem(
        `${AIRBYTE_POLLING_PREFIX}${companyConnectorId}`,
      ) === "true"
    if (!isActivelyPolling) return

    const companyConnector = connectors.find(cc => cc.id === companyConnectorId)

    if (companyConnector?.status === ConnectionStatus.PENDING_RECONNECT) {
      setHasFailed(true)
      stopAirbytePolling(companyConnectorId)

      const errorMessage =
        errorData?.message ||
        `Failed to complete ${connector?.name || "connector"} setup. Please try again.`

      toast.error(errorMessage, {
        toastId: `airbyte-background-error-${connector?.id}`,
      })
    } else if (companyConnector?.status === ConnectionStatus.CONNECTED) {
      setHasFailed(false)
      stopAirbytePolling(companyConnectorId)
    }
  }, [connectors, companyConnectorId, connector, errorData])

  const isGA4Connector = connector?.id === "google_analytics_4_airbyte"

  /**
   * Complete Airbyte setup with additional configuration
   * Handles different connector types (GA4, Instagram, TikTok, etc.)
   */
  const completeSetup = useCallback(
    async (additionalConfig: Record<string, unknown>) => {
      if (!companyConnectorId) {
        throw new Error("No company connector ID provided")
      }

      if (!connector) {
        throw new Error("No connector configuration provided")
      }

      if (!isAirbyteConnector(connector)) {
        throw new Error("Connector is not an Airbyte connector")
      }

      try {
        await completeAirbyteSetup({
          id: companyConnectorId,
          additionalConfig,
        }).unwrap()

        startAirbytePolling(companyConnectorId)

        toast.success(
          `${connector.name} setup started! This may take a few minutes.`,
          {
            toastId: `airbyte-setup-success-${connector.id}`,
          },
        )
      } catch (error: unknown) {
        // Parse backend validation errors
        let errorMessage = `Failed to complete ${connector.name} setup`

        if (hasErrorMessage(error)) {
          errorMessage = error.data.message

          // If there are specific field errors, include them in the message
          if (
            error.data &&
            "errors" in error.data &&
            Array.isArray(error.data.errors) &&
            error.data.errors.length > 0
          ) {
            // Show first error as primary message, others will be shown in form
            errorMessage = error.data.errors.join(", ")
          }
        } else if (error instanceof Error) {
          errorMessage = error.message
        }

        toast.error(errorMessage, {
          toastId: `airbyte-setup-error-${connector.id}`,
        })
        throw error
      }
    },
    [companyConnectorId, connector, completeAirbyteSetup],
  )

  /**
   * Get additional configuration schema for the connector
   * Returns the schema from API response that defines what additional config is needed
   * @param phase - Optional phase filter ('oauth' | 'source'). If not provided, returns all items.
   */
  const getAdditionalConfigSchema = useCallback(
    (phase?: "oauth" | "source") => {
      if (!connector || !isAirbyteConnector(connector)) {
        return []
      }

      const allSchema = airbyteProperties?.additional_config || []

      // Filter out hidden fields (auto-generated, not shown in UI)
      const visibleSchema = allSchema.filter(item => !item.hidden)

      if (!phase) {
        return visibleSchema
      }

      // Filter by phase: oauth phase items have config_phase === 'oauth', source phase items have config_phase !== 'oauth' or undefined
      if (phase === "oauth") {
        return visibleSchema.filter(item => item.config_phase === "oauth")
      }

      // source phase: items without config_phase or with config_phase === 'source'
      return visibleSchema.filter(
        item => !item.config_phase || item.config_phase === "source",
      )
    },
    [connector, airbyteProperties],
  )

  /**
   * Get pre-config schema (oauth phase items)
   */
  const getPreConfigSchema = useCallback(
    () => getAdditionalConfigSchema("oauth"),
    [getAdditionalConfigSchema],
  )

  /**
   * Get source-config schema (source phase items)
   */
  const getSourceConfigSchema = useCallback(
    () => getAdditionalConfigSchema("source"),
    [getAdditionalConfigSchema],
  )

  /**
   * Get existing metadata (previously saved values)
   */
  const getMetadata = useCallback(
    () => airbyteProperties?.metadata || null,
    [airbyteProperties],
  )

  /**
   * Check if connector needs additional configuration
   */
  const needsAdditionalConfig = useCallback(() => {
    if (!connector || !isAirbyteConnector(connector)) {
      return false
    }

    const schema = getAdditionalConfigSchema()
    return schema.length > 0
  }, [connector, getAdditionalConfigSchema])

  return {
    // Setup completion
    completeSetup,
    isCompletingSetup,

    // Loading states
    isLoadingProperties,
    propertiesError: propertiesError ? "Failed to load configuration" : null,

    // Generic additional config
    getAdditionalConfigSchema,
    getPreConfigSchema,
    getSourceConfigSchema,
    getMetadata,
    needsAdditionalConfig,

    // Connector info
    isAirbyteConnector: connector ? isAirbyteConnector(connector) : false,
    isGA4Connector,
  }
}
